import { useCallback, useMemo, useRef, useState } from 'react'

import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { styled } from 'styled-components'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  CarrierPickerDropdown,
  QueryLoadingManager,
  useAccessToken,
  useAccessTokenRoles,
} from '@b-stock/fe-core/bstock-next/components'
import {
  useSelectQuoteMutation,
  useUpdateOrderShippingMutation,
} from '@b-stock/fe-core/bstock-next/mutations'
import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import {
  buyerOrderDetailsQuery,
  getSelectedQuoteQuery,
  quoteSetQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { Button, FormattedMessage, Modal } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, pxToRem } from '@b-stock/fe-core/design-system'
import type { QuoteDto, ShipmentType } from '@b-stock/shipment-api-client'

const ModalBody = styled.div`
  min-width: ${pxToRem(600)};

  @media ${Breakpoints.max.medium} {
    min-width: unset;
  }
`

type ChangeCarrierModalCallbackProps = {
  onClose: () => void
  onChange: (quoteId: string) => void
  isCarrierChangePending?: boolean
}

type CarrierModalInternalProps = {
  listingId: string
  originLocationId: string
  destinationLocationId: string
  shipmentType: ShipmentType
  currentQuote?: QuoteDto | OrchestratedQuote
  accountId: string
}

type UseChangeCarrierModalParams = CarrierModalInternalProps & {
  orderId?: string
  /** When false, the quote-set and selected-quote queries don't run. */
  enabled?: boolean
}

const ChangeCarrierModal = ({
  listingId,
  originLocationId,
  destinationLocationId,
  shipmentType,
  currentQuote,
  accountId,
  onChange,
  onClose,
  isCarrierChangePending,
}: ChangeCarrierModalCallbackProps & CarrierModalInternalProps) => {
  const accessToken = useAccessToken()
  const [selectedQuoteId, setSelectedQuoteId] = useState(currentQuote?._id)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasSubmittedRef = useRef(false)

  const handleCarrierChange = useCallback((quoteId: string) => {
    setSelectedQuoteId(quoteId)
  }, [])

  const handleSubmit = useCallback(() => {
    // Synchronous ref guard so a freakishly fast synchronous double-click (two
    // clicks in the same tick, before React re-renders the now-disabled button)
    // can't fire onChange twice — the state-based isSubmitting can't catch that race.
    if (
      hasSubmittedRef.current ||
      selectedQuoteId === undefined ||
      selectedQuoteId === ''
    ) {
      return
    }
    hasSubmittedRef.current = true
    setIsSubmitting(true)
    onChange(selectedQuoteId)
    onClose()
  }, [selectedQuoteId, onChange, onClose])

  const handleClose = useCallback(() => {
    hasSubmittedRef.current = false
    setIsSubmitting(false)
    onClose()
  }, [onClose])

  const setQuoteQueryResult = quoteSetQuery.useWithOpts(
    [
      {
        listingId,
        // oxlint-disable-next-line typescript/no-deprecated
        originLocationId,
        destinationLocationId,
        shipmentType,
        currentQuote,
        accountId,
      },
    ],
    {
      enabled:
        !!accessToken &&
        !!destinationLocationId &&
        !isCarrierChangePending &&
        !isSubmitting,
    }
  )

  const isDisabled =
    isCarrierChangePending ||
    isSubmitting ||
    selectedQuoteId === undefined ||
    selectedQuoteId === ''

  return (
    <Modal closeModal={isCarrierChangePending ? () => undefined : handleClose}>
      <Modal.Header>
        <FormattedMessage id="ChangeCarrierModal.title" />
      </Modal.Header>
      <Modal.Content>
        <ModalBody>
          <QueryLoadingManager {...setQuoteQueryResult}>
            {({ data }) => (
              <CarrierPickerDropdown
                currentQuote={currentQuote}
                quotes={data}
                onCarrierChange={handleCarrierChange}
              />
            )}
          </QueryLoadingManager>
        </ModalBody>
      </Modal.Content>
      <Modal.Actions>
        <Button
          appearance="primary"
          disabled={isDisabled}
          onClick={handleSubmit}
        >
          <FormattedMessage id="ChangeCarrierModal.confirm" />
        </Button>
        <Button
          appearance="tertiary"
          disabled={isCarrierChangePending}
          onClick={handleClose}
        >
          <FormattedMessage id="Common.cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

const useChangeCarrierModal = ({
  orderId,
  listingId,
  originLocationId,
  destinationLocationId,
  shipmentType,
  currentQuote,
  accountId,
  enabled = true,
}: UseChangeCarrierModalParams) => {
  const accessToken = useAccessToken()
  const isActiveBuyer = useAccessTokenRoles()?.buyer === RoleStatus.Active
  const queryClient = useQueryClient()
  const selectQuoteMutation = useSelectQuoteMutation()
  const updateOrderShippingMutation = useUpdateOrderShippingMutation()

  const quoteSetQueryResult = quoteSetQuery.useWithOpts(
    [
      {
        listingId,
        // oxlint-disable-next-line typescript/no-deprecated
        originLocationId,
        destinationLocationId,
        shipmentType,
        currentQuote,
        accountId,
      },
    ],
    {
      enabled: enabled && !!accessToken && !!destinationLocationId,
      // The query key includes `currentQuote`; selecting a parcel rate changes
      // the selected quote, which churns the key. Keep the prior rate set on
      // screen during that refetch so the inline parcel list never unmounts.
      placeholderData: keepPreviousData,
    }
  )

  // Track selected quote query for loading state. Only an active buyer has
  // SHIPMENT_QUOTE_READ; others 403 on read.
  const selectedQuoteQueryResult = getSelectedQuoteQuery.useWithOpts(
    [listingId, accountId],
    {
      enabled:
        enabled && !!accessToken && !!listingId && !!accountId && isActiveBuyer,
    }
  )

  // Track order details query for loading state (if on order page)
  const orderDetailsQueryResult = buyerOrderDetailsQuery.useWithOpts(
    [orderId ?? ''],
    { enabled: false }
  )

  const hasMultipleCarrierOptions = useMemo(() => {
    const quotes = quoteSetQueryResult.data ?? []
    const distinctCarriers = new Set(
      quotes.map((q) =>
        q.carrier?.accountId
          ? `${q.carrier.accountId}|${q.transportMode ?? ''}`
          : '__no_carrier__'
      )
    )
    return distinctCarriers.size > 1
  }, [quoteSetQueryResult.data])

  const isCheapestCarrier = useMemo(() => {
    const quotes = quoteSetQueryResult.data
    if (!quotes || quotes.length <= 1 || !currentQuote) return false
    const cheapest = quotes.reduce<(typeof quotes)[0] | null>(
      (prev, quote) =>
        !prev || quote.totalPrice < prev.totalPrice ? quote : prev,
      null
    )
    return cheapest?._id === currentQuote._id
  }, [quoteSetQueryResult.data, currentQuote])

  const setSelectedCarrier = useCallback(
    async (quoteId: string) => {
      if (currentQuote) {
        await selectQuoteMutation.mutateAsync({
          quoteId,
          listingId: currentQuote.listingId,
          accountId: currentQuote.accountId,
        })
      }
      try {
        if (orderId) {
          await updateOrderShippingMutation.mutateAsync({ orderId, quoteId })
        }
      } finally {
        // Invalidate selected quote cache
        if (accessToken) {
          await queryClient.invalidateQueries({
            queryKey: getSelectedQuoteQuery.getKey(accessToken, [
              listingId,
              accountId,
            ]),
          })
        }
        // Invalidate quote set cache to refresh available options
        if (accessToken) {
          await queryClient.invalidateQueries({
            queryKey: quoteSetQuery.getKey(accessToken, [
              {
                listingId,
                // oxlint-disable-next-line typescript/no-deprecated
                originLocationId,
                destinationLocationId,
                shipmentType,
                currentQuote,
                accountId,
              },
            ]),
          })
        }
        // Invalidate order details if viewing from order page
        if (orderId && accessToken) {
          await queryClient.invalidateQueries({
            queryKey: buyerOrderDetailsQuery.getKey(accessToken, [orderId]),
          })
        }
      }
    },
    [
      accessToken,
      accountId,
      currentQuote,
      destinationLocationId,
      listingId,
      originLocationId,
      orderId,
      queryClient,
      selectQuoteMutation,
      shipmentType,
      updateOrderShippingMutation,
    ]
  )

  const changeCarrierModalProps = useMemo(
    () => ({
      listingId,
      originLocationId,
      destinationLocationId,
      shipmentType,
      currentQuote,
      accountId,
    }),
    [
      accountId,
      currentQuote,
      destinationLocationId,
      listingId,
      originLocationId,
      shipmentType,
    ]
  )

  return {
    ChangeCarrierModal,
    changeCarrierModalProps,
    setSelectedCarrier,
    isCarrierChangePending:
      selectQuoteMutation.isPending ||
      updateOrderShippingMutation.isPending ||
      quoteSetQueryResult.isFetching ||
      selectedQuoteQueryResult.isFetching ||
      orderDetailsQueryResult.isFetching,
    hasMultipleCarrierOptions,
    isCheapestCarrier,
    // FP-1904: expose the full rate set so callers can render parcel rates inline
    // (the modal lists every QuoteDto rather than opening the carrier dropdown).
    quoteSet: quoteSetQueryResult.data,
    isQuoteSetLoading: quoteSetQueryResult.isLoading,
    isQuoteSetError: quoteSetQueryResult.isError,
  }
}

export default useChangeCarrierModal
